import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import { usePopper } from 'react-popper';
import * as Styled from './Tooltip.styled';
import { trapFocusInElement } from '../../util/util';
import { HtmlElement } from '../../prop-types/Element';

const TRANSITION_DURATION = 100;
export const PLACEMENT_OPTIONS = [
  'top',
  'top-start',
  'top-end',
  'bottom',
  'bottom-start',
  'bottom-end'
];

/**
 * A tooltip that contains any content you want to display in it.
 *
 * It renders either on top or on bottom the element that triggered it to appear.
 *
 * FOR TRACKING- the `label` attribute is supplied by closest ancestor containing a `data-track-label` attribute.
 */
const Tooltip = ({
  anchorElement,
  children,
  dataLabelClose,
  onClose,
  title,
  offset,
  placement,
  action,
  isFlipEnabled,
  textTransform,
  className,
  ...props
}) => {
  const [tooltipElement, setTooltipElement] = useState(null);
  const [tooltipArrowElement, setTooltipArrowElement] = useState(null);

  /**
   * Detects a click outside of the tooltip anchor region
   *
   * @param {SyntheticBaseEvent} e - React SyntheticBaseEvent object
   */
  const handleClickOutside = (e) => {
    if (tooltipElement && !tooltipElement.contains(e.target)) {
      onClose(e, tooltipElement);
    }
  };

  /**
   * If the triggering action is click, bind an event listener to check for clicks outside the tooltip and temporarily disable triggering element
   */
  useEffect(() => {
    if (anchorElement && tooltipElement && action === 'click') {
      anchorElement.setAttribute('style', 'pointer-events: none;');
      document.addEventListener('click', handleClickOutside, { capture: true });
    }

    return () => {
      if (anchorElement && action === 'click') {
        anchorElement.style.removeProperty('pointer-events');
        document.removeEventListener('click', handleClickOutside, {
          capture: true
        });
      }
    };
  }, [anchorElement, tooltipElement]);

  const popperInstance = usePopper(anchorElement, tooltipElement, {
    placement,
    modifiers: [
      { name: 'preventOverflow', options: { padding: 8 } },
      { name: 'arrow', options: { element: tooltipArrowElement } },
      { name: 'offset', enabled: true, options: { offset: [0, offset] } },
      { name: 'flip', enabled: isFlipEnabled }
    ]
  });

  /**
   * Detects escape key press when focused inside tooltip
   *
   * @param {SyntheticBaseEvent} e - React SyntheticBaseEvent
   */
  const handleKeyDown = (e) => {
    const KEYCODE_ESCAPE = 27;
    const isEscapePressed = e.key === 'Escape' || e.code === KEYCODE_ESCAPE;

    if (isEscapePressed) {
      onClose(e, tooltipElement);
    }

    trapFocusInElement(e);
  };

  return (
    <CSSTransition
      in={Boolean(anchorElement)}
      timeout={TRANSITION_DURATION}
      classNames="tool-tip-transition"
      unmountOnExit
      onKeyDown={handleKeyDown}
    >
      <Styled.Tooltip
        className={className}
        data-testid="tooltip"
        position={popperInstance.styles.popper}
        ref={setTooltipElement}
        transitionDuration={TRANSITION_DURATION}
        {...props}
      >
        <Styled.TooltipInnerContainer action={action}>
          <Styled.TooltipArrow
            position={popperInstance.styles.arrow}
            placement={popperInstance.state?.placement}
            ref={setTooltipArrowElement}
          />
          {action === 'click' && (
            <Styled.TooltipHead>
              <p>{title}</p>
              <Styled.ButtonIconWithFocus
                name="close"
                variant="defaultLight"
                onClick={(e) => onClose(e, tooltipElement)}
                data-testid="tooltip-close-button"
                data-label={dataLabelClose}
                aria-label="Close"
              />
            </Styled.TooltipHead>
          )}
          <Styled.TooltipContent textTransform={textTransform}>
            {children}
          </Styled.TooltipContent>
        </Styled.TooltipInnerContainer>
      </Styled.Tooltip>
    </CSSTransition>
  );
};

Tooltip.propTypes = {
  /**
   * The triggering element that the tooltip will mount to, passed as a DOM node.
   *
   * This is also the element that is interacted with to trigger the tooltip open or closed.
   *
   * @example
   *
   * <TooltipContainer>
   *  <ButtonTrigger anchorElement />
   *  <Tooltip />
   * </TooltipContainer>
   */
  anchorElement: PropTypes.instanceOf(HtmlElement),
  /**
   * The main content of the tooltip
   */
  children: PropTypes.node.isRequired,
  /**
   * Data label for the close button for tracking
   */
  dataLabelClose: PropTypes.string,
  /**
   * Amount, in pixels, to move the tooltip away from the reference
   *
   * @see {@link https://popper.js.org/docs/v2/modifiers/offset/}
   */
  offset: PropTypes.number,
  /**
   * Requests the tooltip to close
   */
  onClose: PropTypes.func.isRequired,
  /**
   * A header for the tooltip
   */
  title: PropTypes.string,
  /**
   * Where the tooltip will be position with respect to it's anchorElement.
   *
   * Accepts all valid Popper options
   *
   * @see {@link https://popper.js.org/docs/v2/constructors/#options}
   */
  placement: PropTypes.oneOf(PLACEMENT_OPTIONS),
  /**
   * Describes the triggering event for the tooltip
   *
   * If the action is click, it will bind global event listeners to detect a click outside the tooltip and disable the anchorElement
   *
   * Passing an incorrect action will cause interactivity issues
   */
  action: PropTypes.oneOf(['click', 'hover']),
  /**
   * If there isn't enough room for the tooltip when it renders,
   * it will flip to the opposite side from where it was originally placed
   *
   * If it was placed on the bottom of its anchorElement and there wasn't enough room
   * It would render on top of it instead
   */
  isFlipEnabled: PropTypes.bool,
  /**
   * CSS Text Transform
   */
  textTransform: PropTypes.oneOf([
    'capitalize',
    'uppercase',
    'lowercase',
    'none'
  ]),
  /**
   * enable extended styles via styed components
   */
  className: PropTypes.string
};

Tooltip.defaultProps = {
  dataLabelClose: 'tooltip_close',
  title: '',
  offset: 24,
  placement: 'top',
  anchorElement: null,
  action: 'click',
  isFlipEnabled: true,
  textTransform: 'none',
  className: undefined
};

export default Tooltip;
